import { z } from 'zod';
import { DayHours, ErrorBody, IsoDate, IsoDateTime } from '@yourstore/domain';

/**
 * The staff wire, as this client understands it.
 *
 * It mirrors `apps/core-api/src/wire.ts` by hand and on purpose. Apps do not
 * cross-import (dev.md §3: the dependency arrows run through `packages/`, and
 * `core-api`'s wire module imports `@yourstore/store`, which would drag Drizzle
 * and the `postgres` driver into a browser bundle). What the two files *do*
 * share is `@yourstore/domain`, so the scalars that carry a promise —
 * `IsoDateTime` with an explicit offset, `IsoDate`, `DayHours` — are one
 * definition rather than two lookalikes.
 *
 * Everything the network returns is parsed through these schemas before it
 * reaches the mirror. That is the point of them: the mirror is what the app
 * shows while offline, and a malformed row written into it is a lie the device
 * repeats for as long as the tunnel lasts.
 */

export const SessionRoute = z.object({
  id: z.string(),
  method: z.string(),
  path: z.string(),
  module: z.string().nullable(),
  write: z.boolean(),
});

export const Session = z.object({
  user: z.object({ uid: z.string(), role: z.enum(['owner', 'staff', 'readonly']) }),
  tenant: z.object({
    tenant_id: z.string(),
    name: z.string(),
    timezone: z.string(),
    currency: z.string(),
    status: z.enum(['onboarding', 'importing', 'live', 'paused', 'offboarded']),
  }),
  modules: z.array(z.string()),
  can_write: z.boolean(),
  routes: z.array(SessionRoute),
});
export type Session = z.infer<typeof Session>;

export const WireIdentity = z.object({
  identity_kind: z.enum(['phone', 'email']),
  identity: z.string(),
});
export type WireIdentity = z.infer<typeof WireIdentity>;

export const ClientSummary = z.object({
  client_id: z.string(),
  display_name: z.string(),
  archived: z.boolean(),
  is_walk_in_account: z.boolean(),
  tags: z.array(z.string()),
  identities: z.array(WireIdentity),
  visit_count: z.number(),
  last_visit_at: z.string().nullable(),
});
export type ClientSummary = z.infer<typeof ClientSummary>;

export const Preference = z.object({
  preference_id: z.string(),
  category: z.enum(['preference', 'allergy']),
  label: z.string(),
  detail: z.string().nullable(),
  /** Staff-only. Obligation G7: it never crosses the partner surface. */
  internal: z.boolean(),
  created_at: z.string(),
});
export type Preference = z.infer<typeof Preference>;

export const ClientDetail = ClientSummary.extend({
  birthday: z.string().nullable(),
  notes: z.string().nullable(),
  sms_opt_in: z.boolean(),
  preferences: z.array(Preference),
  anonymized_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  /** Echoed back on a write that carried one (`intents.ts`, `withIntentId`). */
  client_intent_id: z.string().optional(),
});
export type ClientDetail = z.infer<typeof ClientDetail>;

export const ClientList = z.object({ clients: z.array(ClientSummary) });
export const PreferenceList = z.object({ preferences: z.array(Preference) });

export const BookingSource = z.enum(['staff', 'partner', 'import', 'walk_in']);
export type BookingSource = z.infer<typeof BookingSource>;

export const AppointmentStatus = z.enum([
  'confirmed',
  'completed',
  'cancelled',
  'no_show',
  'quarantined',
]);
export type AppointmentStatus = z.infer<typeof AppointmentStatus>;

export const Appointment = z.object({
  appointment_id: z.string(),
  status: AppointmentStatus,
  source: BookingSource,
  slot: z.object({ start: IsoDateTime, end: IsoDateTime, resource_id: z.string() }),
  resource_name: z.string().nullable(),
  service_id: z.string(),
  service_name: z.string(),
  duration_min: z.number(),
  party_size: z.number(),
  price_cents: z.number(),
  discount_cents: z.number(),
  value_cents: z.number(),
  notes: z.string().nullable(),
  provider_ref: z.string().nullable(),
  client: z.object({
    client_id: z.string(),
    display_name: z.string(),
    phone: z.string().nullable(),
  }),
  created_at: z.string(),
  updated_at: z.string(),
  client_intent_id: z.string().optional(),
  fee_cents: z.number().optional(),
  cancelled_at: z.string().optional(),
});
export type Appointment = z.infer<typeof Appointment>;

export const AppointmentDay = z.object({
  date_from: IsoDate,
  date_to: IsoDate,
  timezone: z.string(),
  appointments: z.array(Appointment),
});
export type AppointmentDay = z.infer<typeof AppointmentDay>;

export const Service = z.object({
  service_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  price_cents: z.number(),
  duration_min: z.number(),
  buffer_before_min: z.number(),
  buffer_after_min: z.number(),
  max_party: z.number(),
  active: z.boolean(),
  bookable_by_partner: z.boolean(),
  bookable_online: z.boolean(),
  details: z.array(z.string()),
  resource_ids: z.array(z.string()),
  catalog_version: z.number().optional(),
  client_intent_id: z.string().optional(),
});
export type Service = z.infer<typeof Service>;

export const ServiceList = z.object({ services: z.array(Service) });

export const Resource = z.object({
  resource_id: z.string(),
  name: z.string(),
  kind: z.enum(['room', 'person', 'equipment', 'table_pool']),
  capacity: z.number(),
  units: z.number(),
  active: z.boolean(),
  hours: z.array(DayHours).nullable(),
  catalog_version: z.number().optional(),
  client_intent_id: z.string().optional(),
});
export type Resource = z.infer<typeof Resource>;

export const ResourceList = z.object({ resources: z.array(Resource) });

export const Hours = z.object({ timezone: z.string(), hours: z.array(DayHours) });
export type Hours = z.infer<typeof Hours>;

export const HoursWritten = z.object({
  hours: z.array(DayHours),
  client_intent_id: z.string().optional(),
});

export const Blackout = z.object({
  blackout_id: z.string(),
  resource_id: z.string().nullable(),
  start: IsoDateTime,
  end: IsoDateTime,
  reason: z.string().nullable(),
  client_intent_id: z.string().optional(),
});
export type Blackout = z.infer<typeof Blackout>;

export const BlackoutList = z.object({ blackouts: z.array(Blackout) });

export const Deleted = z.object({
  deleted: z.boolean(),
  client_intent_id: z.string().optional(),
});

export const OfferedSlot = z.object({
  start: IsoDateTime,
  end: IsoDateTime,
  resource_id: z.string(),
  service_id: z.string(),
  capacity: z.number(),
});
export type OfferedSlot = z.infer<typeof OfferedSlot>;

export const Availability = z.object({
  authority: z.enum(['live', 'approximate']),
  slots: z.array(OfferedSlot),
  alternatives: z.array(OfferedSlot),
  summary: z.string(),
});
export type Availability = z.infer<typeof Availability>;

/**
 * The `409` that becomes an owner task (spec §8.3).
 *
 * `source` is the field the copy hangs on. "The concierge sold this while you
 * were offline" and "you booked this on the other iPad" are different sentences
 * and `enrichConflict` in `routes/appointments.ts` is what makes the difference
 * knowable; parsing it loosely here would throw that away.
 */
export const ConflictingAppointment = z.object({
  appointment_id: z.string(),
  status: z.string(),
  source: BookingSource,
  client_name: z.string().nullable(),
  service_name: z.string().nullable(),
  start: z.string(),
  end: z.string(),
});
export type ConflictingAppointment = z.infer<typeof ConflictingAppointment>;

export const SlotTakenDetail = z.object({
  reason: z.literal('slot_taken'),
  client_intent_id: z.string().optional(),
  resource_id: z.string(),
  requested: z.object({ start: z.string(), end: z.string() }),
  conflicts: z.array(ConflictingAppointment),
});
export type SlotTakenDetail = z.infer<typeof SlotTakenDetail>;

export { ErrorBody };

/** One frame of `GET /v1/events/stream`, after the SSE envelope is stripped. */
export const StreamEventData = z.object({
  event_id: z.string(),
  type: z.string(),
  subject_id: z.string().nullable(),
  occurred_at: z.string(),
  payload: z.unknown().optional(),
});
export type StreamEventData = z.infer<typeof StreamEventData>;
